// Update these imports at the top of your file
import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, School, Calendar, FileText, BookOpen, Camera, X } from 'lucide-react';
import AuthContext from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Award } from 'lucide-react';
import ProfilePicture from '../../components/ProfilePicture';

const ProfilePage = () => {
  const { user, updateUser } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [major, setMajor] = useState(user?.major || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.name && name !== user.name) {
      setName(user.name);
    }
  }, [user?.name]);

  useEffect(() => {
    if (user?.major !== undefined && major !== user.major) {
      setMajor(user.major || '');
    }
  }, [user?.major]);

  useEffect(() => {
    if (user?.bio !== undefined && bio !== user.bio) {
      setBio(user.bio || '');
    }
  }, [user?.bio]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Please log in to view your profile.
          </p>
          <Link 
            to="/login" 
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|png|gif)$/)) {
      toast.error('Invalid file type. Only JPEG, PNG, and GIF images are supported.');
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 5MB.');
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Store the file for later upload
    setSelectedFile(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const resetProfilePicture = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    try {
      setLoading(true);
      
      // If there's a profile picture to upload, do that first
      let profilePictureUrl = user.profilePictureUrl;
      
      if (selectedFile) {
        const formData = new FormData();
        formData.append('profilePicture', selectedFile);
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me/profile-picture`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload profile picture');
        }
        
        const uploadData = await uploadResponse.json();
        profilePictureUrl = uploadData.profilePictureUrl;
      }
      
      // Now update the profile with the name
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name,
          major: major.trim() || null,
          bio: bio.trim() || null
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
      
      const userData = await response.json();
      
      // Update user in context with combined data
      updateUser({
        ...userData,
        profilePictureUrl
      });
      
      setIsEditing(false);
      setPreviewUrl(null);
      setSelectedFile(null);
      
      toast.success(selectedFile ? 
        'Profile updated with new profile picture' : 
        'Profile updated successfully'
      );
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">My Profile</h1>
      
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {/* Profile header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <ProfilePicture url={user.profilePictureUrl} size="md" />
            <div className="ml-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {user.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Member since {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
              </p>
            </div>
          </div>
          <div className="mt-2 flex items-center">
            <Award className={`h-5 w-5 ${getMeritColor(user.merit || 0)} mr-2`} />
            <div>
              <p className={`font-medium ${getMeritColor(user.merit || 0)}`}>
                {user.merit || 0} Merit
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {getMeritLevel(user.merit || 0)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Profile content */}
        <div className="px-4 py-5 sm:px-6">
          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Profile Picture Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Profile Picture
                  </label>
                  <div className="flex items-center space-x-6">
                    {/* Current or Preview Picture */}
                    {previewUrl ? (
                      <div className="relative">
                        <div className="h-24 w-24 rounded-full overflow-hidden">
                          <img 
                            src={previewUrl} 
                            alt="Profile preview" 
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={resetProfilePicture}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <ProfilePicture url={user.profilePictureUrl} size="lg" />
                    )}
                    
                    {/* Upload Controls */}
                    <div className="flex flex-col space-y-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,image/gif"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={handleUploadClick}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        {previewUrl ? 'Change Image' : 'Select Image'}
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        JPG, PNG or GIF. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label htmlFor="major" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Major
                  </label>
                  <input
                    type="text"
                    id="major"
                    value={major}
                    onChange={(e) => setMajor(e.target.value)}
                    placeholder="e.g., Computer Science, Biology, etc."
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell others about yourself..."
                    rows={4}
                    maxLength={500}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {bio.length}/500 characters
                  </p>
                </div>
                
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      setName(user.name);
                      setMajor(user.major || '');
                      setBio(user.bio || '');
                      setIsEditing(false);
                      setPreviewUrl(null);
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                      loading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center mb-1">
                    <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</h3>
                  </div>
                  <p className="text-base text-gray-900 dark:text-white">{user.email}</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-1">
                    <School className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">School</h3>
                  </div>
                  <p className="text-base text-gray-900 dark:text-white">{user.school?.name || 'Not specified'}</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-1">
                    <User className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Role</h3>
                  </div>
                  <p className="text-base text-gray-900 dark:text-white capitalize">{user.role}</p>
                </div>
                
                {user.major && (
                  <div>
                    <div className="flex items-center mb-1">
                      <BookOpen className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Major</h3>
                    </div>
                    <p className="text-base text-gray-900 dark:text-white">{user.major}</p>
                  </div>
                )}
                
                {user.bio && (
                  <div>
                    <div className="flex items-center mb-1">
                      <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">About</h3>
                    </div>
                    <p className="text-base text-gray-900 dark:text-white whitespace-pre-wrap">{user.bio}</p>
                  </div>
                )}
                
                <div>
                  <div className="flex items-center mb-1">
                    <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Joined</h3>
                  </div>
                  <p className="text-base text-gray-900 dark:text-white">
                    {user.createdAt ? formatDate(user.createdAt, true) : 'N/A'}
                  </p>
                </div>
                
                <div className="pt-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Activity section - unchanged */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                My Courses
              </h3>
              <Link
                to="/my-courses"
                className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-500"
              >
                View All
              </Link>
            </div>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center">
              <BookOpen className="h-10 w-10 text-red-500 mr-4" />
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {user.subscriptionCount || 0} Subscribed Courses
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Courses you're following for updates
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                My Materials
              </h3>
              <Link
                to="/my-materials"
                className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-500"
              >
                View All
              </Link>
            </div>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center">
              <FileText className="h-10 w-10 text-blue-500 mr-4" />
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {user.materialCount || 0} Uploaded Materials
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Notes, exams, and other resources you've shared
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Settings link */}
      <div className="mt-6 text-center">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          Account Settings
          <svg className="ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

// The utility functions remain unchanged
// Function to get merit level title based on merit score
const getMeritLevel = (merit) => {
  if (merit >= 1000) return 'Scholar';
  if (merit >= 500) return 'Academic';
  if (merit >= 250) return 'Contributor';
  if (merit >= 100) return 'Student';
  if (merit >= 50) return 'Novice';
  return 'Newcomer';
};

// Function to get merit color based on merit score
const getMeritColor = (merit) => {
  if (merit >= 1000) return 'text-purple-600 dark:text-purple-400';
  if (merit >= 500) return 'text-blue-600 dark:text-blue-400';
  if (merit >= 250) return 'text-green-600 dark:text-green-400';
  if (merit >= 100) return 'text-yellow-600 dark:text-yellow-400';
  if (merit >= 50) return 'text-orange-600 dark:text-orange-400';
  return 'text-gray-600 dark:text-gray-400';
};

// This handles different date formats including timestamp numbers
const formatDate = (dateValue, longFormat = false) => {
  let date;
  
  try {
    // If it's a number (timestamp), convert it properly
    if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } 
    // If it's a string that could be a number
    else if (typeof dateValue === 'string' && !isNaN(Number(dateValue))) {
      date = new Date(Number(dateValue));
    }
    // Otherwise try to parse it as a date string
    else {
      date = new Date(dateValue);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Format the date according to the specified format
    if (longFormat) {
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return date.toLocaleDateString();
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

export default ProfilePage;